import { ChevronDown } from "lucide-react";
import type React from "react";
import { useCallback, useState } from "react";

import { Button } from "@src/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/shared/components/ui/collapsible";
import { cn } from "@src/shared/lib/utils";

export interface CollapsiblePanelProps {
  label: string;
  children: React.ReactNode;
  count?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
}

export function CollapsiblePanel({
  label,
  children,
  count,
  open,
  defaultOpen = false,
  onOpenChange,
  className,
  contentClassName,
  contentStyle,
}: CollapsiblePanelProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = open ?? uncontrolledOpen;
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setOpen}
      className="h-full min-h-0 w-full"
    >
      <div
        className={cn(
          "relative flex w-full shrink-0 flex-col overflow-hidden rounded-[10px] border border-border bg-background after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-10 after:border-border after:border-b after:content-['']",
          className,
        )}
      >
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="flex h-9 w-full justify-between rounded-none bg-surface px-3 text-left hover:bg-surface"
            aria-label={isOpen ? `Collapse ${label}` : `Expand ${label}`}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="font-mono text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                {label}
              </span>
              {count != null ? (
                <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                  {count}
                </span>
              ) : null}
            </span>
            <ChevronDown
              className={cn(
                "size-3.5 text-muted-foreground transition-transform",
                isOpen && "rotate-180",
              )}
              aria-hidden="true"
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent
          className={cn(isOpen && "min-h-0 flex-1 overflow-hidden")}
        >
          <div
            className={cn(isOpen && "h-full min-h-0", contentClassName)}
            style={contentStyle}
          >
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
